import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, TrendingUp, Edit } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Investments({ user, onLogout }) {
  const [investments, setInvestments] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    type: 'stocks',
    amount: '',
    current_value: '',
    purchase_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/investments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(response.data);
    } catch (error) {
      toast.error('Failed to fetch investments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        current_value: parseFloat(formData.current_value),
        purchase_date: new Date(formData.purchase_date).toISOString()
      };

      if (editingInvestment) {
        // Update existing investment
        await axios.put(
          `${API}/investments/${editingInvestment.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Investment updated successfully!');
      } else {
        // Create new investment
        await axios.post(
          `${API}/investments`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Investment added successfully!');
      }

      setOpen(false);
      resetForm();
      fetchInvestments();
    } catch (error) {
      toast.error(editingInvestment ? 'Failed to update investment' : 'Failed to add investment');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (investment) => {
    setEditingInvestment(investment);
    
    // Handle date safely - check purchase_date field
    let dateValue = new Date().toISOString().split('T')[0]; // default to today
    try {
      if (investment.purchase_date) {
        const parsedDate = new Date(investment.purchase_date);
        if (!isNaN(parsedDate.getTime())) {
          dateValue = parsedDate.toISOString().split('T')[0];
        }
      }
    } catch (error) {
      console.error('Date parsing error:', error);
    }
    
    setFormData({
      name: investment.name,
      type: investment.type,
      amount: investment.amount.toString(),
      current_value: investment.current_value.toString(),
      purchase_date: dateValue
    });
    setOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'stocks',
      amount: '',
      current_value: '',
      purchase_date: new Date().toISOString().split('T')[0]
    });
    setEditingInvestment(null);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/investments/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Investment deleted!');
      fetchInvestments();
    } catch (error) {
      toast.error('Failed to delete investment');
    }
  };

  // Filter investments based on selected filter
  const filteredInvestments = selectedFilter === 'all' 
    ? investments 
    : investments.filter(inv => inv.type === selectedFilter);

  // Calculate portfolio summary
  const portfolioSummary = investments.reduce((acc, inv) => {
    acc.totalInvested += inv.amount;
    acc.currentValue += inv.current_value;
    return acc;
  }, { totalInvested: 0, currentValue: 0 });

  const totalReturns = portfolioSummary.currentValue - portfolioSummary.totalInvested;
  const totalReturnsPercent = portfolioSummary.totalInvested > 0 
    ? (totalReturns / portfolioSummary.totalInvested) * 100 
    : 0;

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'stocks', label: 'Stocks' },
    { value: 'mutual_funds', label: 'Mutual Funds' },
    { value: 'fixed_deposit', label: 'Fixed Deposit' },
    { value: 'ppf', label: 'PPF' },
    { value: 'epf', label: 'EPF' },
    { value: 'nps', label: 'NPS' },
    { value: 'nsc', label: 'NSC' },
    { value: 'gold', label: 'Gold' },
    { value: 'bonds', label: 'Bonds' },
    { value: 'crypto', label: 'Crypto' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900" data-testid="investments-page">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/investments" />

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2" data-testid="investments-title">
              Investment Portfolio
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Track your investment performance</p>
          </div>

          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-full"
                data-testid="add-investment-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Investment
              </Button>
            </DialogTrigger>
            <DialogContent className="dark:bg-gray-800" data-testid="investment-dialog">
              <DialogHeader>
                <DialogTitle className="dark:text-gray-100">{editingInvestment ? 'Edit Investment' : 'Add New Investment'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="dark:text-gray-300">Investment Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Apple Stock, Bitcoin"
                    required
                    data-testid="name-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Type</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger data-testid="type-select" className="dark:bg-gray-700 dark:text-gray-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent 
                      position="popper"
                      sideOffset={5}
                      className="dark:bg-gray-800 dark:border-gray-700 z-50"
                    >
                      <SelectItem value="stocks" className="dark:text-gray-100 dark:hover:bg-gray-700">Stocks</SelectItem>
                      <SelectItem value="mutual_funds" className="dark:text-gray-100 dark:hover:bg-gray-700">Mutual Funds</SelectItem>
                      <SelectItem value="bonds" className="dark:text-gray-100 dark:hover:bg-gray-700">Bonds</SelectItem>
                      <SelectItem value="fixed_deposit" className="dark:text-gray-100 dark:hover:bg-gray-700">Fixed Deposit (FD)</SelectItem>
                      <SelectItem value="ppf" className="dark:text-gray-100 dark:hover:bg-gray-700">PPF</SelectItem>
                      <SelectItem value="epf" className="dark:text-gray-100 dark:hover:bg-gray-700">EPF</SelectItem>
                      <SelectItem value="nps" className="dark:text-gray-100 dark:hover:bg-gray-700">NPS</SelectItem>
                      <SelectItem value="nsc" className="dark:text-gray-100 dark:hover:bg-gray-700">NSC</SelectItem>
                      <SelectItem value="crypto" className="dark:text-gray-100 dark:hover:bg-gray-700">Cryptocurrency</SelectItem>
                      <SelectItem value="gold" className="dark:text-gray-100 dark:hover:bg-gray-700">Gold</SelectItem>
                      <SelectItem value="real_estate" className="dark:text-gray-100 dark:hover:bg-gray-700">Real Estate</SelectItem>
                      <SelectItem value="other" className="dark:text-gray-100 dark:hover:bg-gray-700">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="dark:text-gray-300">Purchase Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="amount-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Current Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.current_value}
                    onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="current-value-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <Label className="dark:text-gray-300">Purchase Date</Label>
                  <Input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="purchase-date-input"
                    className="dark:bg-gray-700 dark:text-gray-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    💡 Tip: Click on month/year in calendar to jump quickly
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  data-testid="submit-investment"
                >
                  {loading ? (editingInvestment ? 'Updating...' : 'Adding...') : (editingInvestment ? 'Update Investment' : 'Add Investment')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Portfolio Summary Card */}
        {investments.length > 0 && (
          <Card className="glass p-6 mb-6 dark:bg-gray-800">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Portfolio Summary</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Invested</p>
                <p className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 break-words">
                  {formatCurrency(portfolioSummary.totalInvested, user?.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Value</p>
                <p className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 break-words">
                  {formatCurrency(portfolioSummary.currentValue, user?.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Returns</p>
                <p className={`text-xl md:text-2xl font-bold break-words ${totalReturns >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {totalReturns >= 0 ? '+' : ''}{formatCurrency(Math.abs(totalReturns), user?.currency)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Returns %</p>
                <p className={`text-xl md:text-2xl font-bold break-words ${totalReturnsPercent >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {totalReturnsPercent >= 0 ? '+' : ''}{totalReturnsPercent.toFixed(2)}%
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Filter Buttons */}
        {investments.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap gap-2">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedFilter(option.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedFilter === option.value
                      ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="investments-grid">
          {filteredInvestments.length > 0 ? (
            filteredInvestments.map((investment) => {
              const returns = investment.current_value - investment.amount;
              const returnsPercent = (returns / investment.amount) * 100;
              return (
                <Card key={investment.id} className="glass p-6 card-hover dark:bg-gray-800" data-testid={`investment-item-${investment.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(investment)}
                        className="text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                        data-testid={`edit-investment-${investment.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(investment.id)}
                        className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                        data-testid={`delete-investment-${investment.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">{investment.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 capitalize mb-4">{investment.type.replace('_', ' ')}</p>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Purchase:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(investment.amount, user?.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Current:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-200">{formatCurrency(investment.current_value, user?.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Returns:</span>
                      <span className={`font-semibold ${returns >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {returns >= 0 ? '+' : ''}{formatCurrency(Math.abs(returns), user?.currency)} ({returnsPercent.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12" data-testid="no-investments">
              <p className="text-gray-500 dark:text-gray-400">
                {selectedFilter === 'all' 
                  ? 'No investments yet. Add your first investment!' 
                  : `No ${filterOptions.find(f => f.value === selectedFilter)?.label} investments found.`
                }
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}